import kebabCase from 'lodash/kebabCase'
import keyBy from 'lodash/keyBy'
import pickBy from 'lodash/pickBy'
import axios from 'axios'
import io from 'socket.io-client'
import logger from './logger'
import objects from './objects'
import store from './store'
import router from './router'

const api = {
  options: {},
  withOptions(options = {}) {
    return {...api, options}
  },
  request(options = {}) {
    let time = Date.now()

    options = {
      headers: store.state.token ? {Authorization: `Bearer ${store.state.token}`} : {},
      timeout: store.getters.config.apiTimeout,
      ...this.options,
      ...options
    }

    options.url = options.url || `${store.getters.config.apiAddress}/${options.path}`

    logger.info(`🔀 ${options.method} ${options.url}`, {options})

    // define the cancel token to let cancel the request
    // options.cancelToken = new axios.CancelToken(newCancel => {
    //   this.cancel = newCancel
    // })

    return axios(options)
      .finally(() => {
        time = Date.now() - time
      })
      .then(response => {
        if (response.data.error) {
          throw new Error(response.data.error)
        }
        logger.success(`✅ ${options.method} ${options.url} ⌛ ${time} ms`, {result: response.data.result || response.data})
        return response.data.result || response.data
      })
      .catch(response => {
        if (response instanceof axios.Cancel) {
          logger.warning(`🚧 ${options.method} ${options.url} The request has been canceled! ⌛ ${time} ms`)
          return
        }
        const error = response.response ? response.response.data.message : (response.message || 'Unknown Error')
        logger.fail(`⛔ ${options.method} ${options.url} ${error} ⌛ ${time} ms`)
        if (response.response && response.response.status === 402) {
          router.history.push({
            path: '/settings/plans',
            query: { redirect: router.currentRoute.path }
          })
        }
        if (response.response && response.response.status === 401) {
          store.dispatch('logout')
          router.history.push({
            path: '/user/login',
            query: { redirect: router.currentRoute.path }
          })
          return
        }
        throw new Error(error)
      })
  },

  get(path, data, options) {
    return this.request({method: 'GET', path, ...data, ...options})
  },
  post(path, data, options) {
    return this.request({method: 'POST', path, ...data, ...options})
  },
  put(path, data, options) {
    return this.request({method: 'PUT', path, ...data, ...options})
  },
  delete(path, data, options) {
    return this.request({method: 'DELETE', path, ...data, ...options})
  },

  support(data) {
    const formData = Object.entries(data).map(([key, val]) => `${key}=${encodeURIComponent(val)}`).join('&');
    return this.post('support', {data: formData})
  },

  connect() {
    return this.fetchData().then(() => {
      this.socket.io.opts.query = {token: store.state.token}
      this.socket.open()
    })
  },
  reconnect() {
    this.disconnect()
    this.connect()
  },
  disconnect() {
    this.socket.io.opts.query = {}
    this.socket.close()
  },

  fetchData() {
    return Promise.all([this.getDict(), this.getEntities()]).then(([dict, entities]) => {
      if (dict && entities) {
        store.commit('update', {
          account: dict.acc,
          member: dict.member,
          user: dict.user,
          instancesById: keyBy(dict.instances.map(objects.Instance.create), 'id'),
          regionsById: keyBy(dict.regions.map(objects.Region.create), 'id'),
          tariffsById: keyBy(dict.tariffs.map(objects.Tariff.create), 'id'),
          accountsById: keyBy(entities.accounts.map(objects.Account.create), 'id'),
          containersById: keyBy(entities.containers.map(objects.Container.create), 'id'),
          hostsById: keyBy(entities.hosts.map(objects.Host.create), 'id'),
          keysById: keyBy(entities.keys.map(objects.Key.create), 'id'),
          membersById: keyBy(entities.members.map(objects.Member.create), 'id'),
          projectsById: keyBy(entities.projects.map(objects.Project.create), 'id'),
          providersById: keyBy(entities.providers.map(objects.Provider.create), 'id'),
          rolesById: keyBy(entities.roles.map(objects.Role.create), 'id'),
          servicesById: keyBy(entities.services.map(objects.Service.create), 'id'),
        })
      } else {
        logger.warning('fetchData failed')
      }
    })
  },
  login(email, password) {
    return this.post('login', {data: {email, password}})
  },
  register(email, password, invitationId = null) {
    return this.post('register', {data: {email, password, invitationId}})
  },
  registerConfirm(email, confirmId) {
    return this.post('registerConfirm', {data: {email, confirmId}})
  },
  recovery(email) {
    return this.post('recovery', {data: {email}})
  },
  recoveryConfirm(email, recoveryId, password) {
    return this.post('recoveryConfirm', {data: {email, recoveryId, password}})
  },
  accept(email, invitationId) {
    return this.post('accept', {data: {email, invitationId}})
  },

  thirdPartyLogin(method) {
    const params = 'width=1000,height=650,menubar=no,location=yes,resizable=no,scrollbars=no,status=no'
    window.open(`${store.getters.config.apiAddress}/auth/${method}`, null, params)
  },
  oauth(method, providerId) {
    const params = 'width=1000,height=650,menubar=no,location=yes,resizable=no,scrollbars=no,status=no'
    if (providerId) {
      method += `?providerId=${providerId}`
    }
    window.open(`${store.getters.config.apiAddress}/oauth/${method}`, null, params)
  },

  getDict() {
    if (store.getters.config.offline) {
      return Promise.resolve({instance: [], regions: [], tariffs: []})
    }
    return this.get('v1/dict')
  },
  getEntities() {
    if (store.getters.config.offline) {
      return Promise.resolve({accounts: [], containers: [], hosts: [], keys: [], members: [], projects: [], providers: [], roles: [], services: []})
    }
    return this.get('v1/acc/entities')
  },

  getUserInfo(token) {
    if (token) {
      return this.get('v1/user/me', {}, {headers: {Authorization: `Bearer ${token}`}})
    }
    return this.get('v1/user/me')
  },
  updateUserData(data) {
    return this.put('v1/user/data', {data})
  },
  changeUserPassword(data) {
    return this.put('v1/user/password', {data})
  },
  createSubscriptionLink(data) {
    return this.post('v1/acc/subscription', {data})
  },
  updateScubscription(data) {
    return this.put('v1/acc/subscription', {data})
  },
  cancelSubscription() {
    return this.delete('v1/acc/subscription')
  },
  setDefaultAccount(id) {
    return this.put(`v1/user/defaultAcc`, {params: {id}})
  },

  createAccount(data) {
    return this.post('v1/acc', {data})
  },
  updateAccount(data) {
    return this.put('v1/acc', {data})
  },
  deleteAccount() {
    return this.delete(`v1/acc`)
  },
  leaveAccount() {
    return this.delete('v1/member/me')
  },
  getAccountPayments(params = {}) {
    return this.get(`v1/acc/payments`, {params})
  },
  createMember(data) {
    return this.post('v1/member', {data})
  },
  updateMember(memberId, data) {
    return this.put(`v1/member/${memberId}`, {data})
  },
  deleteMember(memberId) {
    return this.delete(`v1/member/${memberId}`)
  },
  createRole(data) {
    return this.post('v1/role', {data})
  },
  updateRole(roleId, data) {
    return this.put(`v1/role/${roleId}`, {data})
  },
  deleteRole(roleId) {
    return this.delete(`v1/role/${roleId}`)
  },

  createProject(data) {
    return this.post(`v1/project`, {data})
  },
  updateProject(projectId, data) {
    return this.put(`v1/project/${projectId}`, {data})
  },
  destroyProject(projectId) {
    return this.delete(`v1/project/${projectId}`)
  },
  updateProjectRules(projectId, data) {
    return this.put(`v1/project/${projectId}/rules`, {data})
  },
  importStackLink(url) {
    return this.post(`v1/project/import`, {params: {url}})
  },
  importStackFile(archive) {
    const data = new FormData();
    data.append('archive', archive);
    return this.post(`v1/project/import`, {data})
  },
  checkProjectName(value) {
    return this.get('v1/project/name', {params: {value}})
  },

  createProvider(type, data) {
    return this.post(`v1/provider/${type}`, {data})
  },
  updateProvider(providerType, providerId, data) {
    return this.put(`v1/provider/${providerType}/${providerId}`, {data})
  },
  destroyProvider(providerId) {
    return this.delete(`v1/provider/${providerId}`)
  },

  createHost(providerType, data) {
    return this.post(`v1/host/${providerType}`, {data})
  },
  createDemoHosts(data) {
    return this.createHost('demo', data)
  },
  applyHostAction(hostId, action, params = {}) {
    return this.put(`v1/host/${hostId}/apply`, {params: {action: kebabCase(action), ...params}})
  },
  destroyHost(hostId, force = false) {
    return this.delete(`v1/host/${hostId}`, {params: {force}})
  },
  updateHostInstance(hostId, instance, reversible = true) {
    const options = reversible ? [] : ['resizeDisk']
    return this.put(`v1/host/${hostId}/resize`, {params: {instance, options}})
  },
  updateHostDisk(hostId, size) {
    return this.put(`v1/host/${hostId}/disk`, {params: {size}})
  },
  cancelHostTask(hostId) {
    return this.delete(`v1/host/${hostId}/task`)
  },
  saveHostKeys(hostId, keys) {
    return this.put(`v1/host/${hostId}/keys`, {data: keys})
  },
  fetchHostLogs(hostId, filter = {}) {
    return this.get('', {params: {id: hostId, target: 'host', filter}}, {url: `${store.getters.config.wsAddress}/logs`})
  },
  fetchHostStats(hostId, params = {}) {
    return this.get('', {params: {id: hostId, target: 'host', ...params}}, {url: `${store.getters.config.wsAddress}/stats`})
  },
  updateHostRules(hostId, data) {
    return this.put(`v1/host/${hostId}/rules`, {data})
  },
  checkHostName(value) {
    return this.get('v1/host/name', {params: {value}})
  },
  getHostRemotePorts(hosts) {
    return this.get('v1/host/remotePorts', {params: {hosts}})
  },

  createService(type, data) {
    return this.post(`v1/service/${type}`, {data})
  },
  applyServiceActions(serviceId, actions = []) {
    return this.put(`v1/service/${serviceId}/apply`, {params: {actions: actions.map(kebabCase)}})
  },
  updateService(serviceId, serviceType, data) {
    return this.put(`v1/service/${serviceType}/${serviceId}`, {data})
  },
  updateServiceLogProvider(serviceId, params) {
    return this.put(`v1/service/${serviceId}/logProvider`, {params})
  },
  updateServiceCrons(serviceId, data) {
    return this.put(`v1/service/${serviceId}/cron`, {data})
  },
  destroyService(serviceId, force = false) {
    return this.delete(`v1/service/${serviceId}`, {params: {force}})
  },
  migrateService(serviceId, serviceType, data) {
    return this.put(`v1/service/${serviceType}/${serviceId}/migrate`, {data})
  },
  moveService(serviceId, project) {
    return this.put(`v1/service/${serviceId}/changeProject`, {params: {project}})
  },
  scaleService(serviceId, data) {
    return this.put(`v1/service/${serviceId}/scale`, {data});
  },
  cancelServiceTask(serviceId) {
    return this.delete(`v1/service/${serviceId}/task`)
  },
  fetchServiceLogs(serviceId, filter = {}) {
    return this.get('', {params: {id: serviceId, target: 'service', filter}}, {url: `${store.getters.config.wsAddress}/logs`})
  },
  execServiceCommands(serviceId, data = {}) {
    return this.put(`v1/service/${serviceId}/execCommands`, {data})
  },
  getServiceWebhook(serviceId, regenerate = false) {
    return this.get(`v1/service/${serviceId}/hook`, {params: {regenerate}})
  },
  resetServicePassword(serviceId, password) {
    return this.put(`v1/service/${serviceId}/resetPassword`, {data: {password}})
  },
  backupService(serviceId, data) {
    return this.put(`v1/service/${serviceId}/backup`, {data})
  },
  restoreService(serviceId, data) {
    return this.put(`v1/service/${serviceId}/recover`, {data})
  },
  recreateService(serviceId) {
    return this.put(`v1/service/${serviceId}/recreate`)
  },
  updateServiceRules(serviceId, data) {
    return this.put(`v1/service/${serviceId}/rules`, {data})
  },
  checkServiceName(value) {
    return this.get('v1/service/name', {params: {value}})
  },

  applyContainerAction(containerId, action) {
    return this.put(`v1/container/${containerId}/apply`, {params: {action: kebabCase(action)}})
  },
  destroyContainer(containerId) {
    return this.delete(`v1/container/${containerId}`)
  },
  cancelContainerTask(containerId) {
    return this.delete(`v1/container/${containerId}/task`)
  },
  fetchContainerLogs(containerId, filter = {}) {
    return this.get('', {params: {id: containerId, target: 'container', filter}}, {url: `${store.getters.config.wsAddress}/logs`})
  },
  fetchContainerStats(containerId, params = {}) {
    return this.get('', {params: {id: containerId, target: 'container', ...params}}, {url: `${store.getters.config.wsAddress}/stats`})
  },
  execContainerCommands(containerId, data = {}) {
    return this.put(`v1/container/${containerId}/execCommands`, {data})
  },
  moveContainer(containerId, hostId) {
    return this.put(`v1/container/${containerId}/move`, {params: {hostId}})
  },
  moveContainerConfirm(containerId) {
    return this.put(`v1/container/${containerId}/moveCommit`)
  },
  moveContainerCancel(containerId) {
    return this.put(`v1/container/${containerId}/moveRollback`)
  },

  createKey(data) {
    return this.post(`v1/key`, {data})
  },
  destroyKey(keyId) {
    return this.delete(`v1/key/${keyId}`)
  },

  fetchCronLogs(cronId) {
    return this.get(`v1/cron/${cronId}/logs`)
  },

  upload(archive, path = 'upload') {
    if (archive) {
      const formData = new FormData()
      formData.append('archive', archive)
      return axios.post(`${store.getters.config.apiAddress}/v1/${path}`, formData, {
        headers: {
          'Authorization': `Bearer ${store.state.token}`,
          'Content-Type': 'multipart/form-data',
        },
      })
    }
  },
}

api.socket = io(`${store.getters.config.wsAddress}/events`, {
  autoConnect: false,
  query: `token=${store.state.token}`,
  transports: ['websocket'],
})

api.subscriptions = {
  hostStats: {},
  containerStats: {},
  subscribedHostIds: [],
  subscribedContainerIds: [],
  updateHostStats() {
    const subscribedHostIds = Object.keys(pickBy(api.subscriptions.hostStats, counter => counter > 0))
    if (this.subscribedHostIds.join() !== subscribedHostIds.join()) {
      this.subscribedHostIds = subscribedHostIds
      if (subscribedHostIds.length) {
        logger.event('📢 subscribe/stats', {ids: subscribedHostIds, target: 'host'})
        api.socket.emit('subscribe/stats', {ids: subscribedHostIds, target: 'host'})
      } else {
        logger.event('📢 unsubscribe/stats', {target: 'host'})
        api.socket.emit('unsubscribe/stats', {target: 'host'})
      }
    }
  },
  updateContainerStats() {
    const subscribedContainerIds = Object.keys(pickBy(api.subscriptions.containerStats, counter => counter > 0))
    if (this.subscribedContainerIds.join() !== subscribedContainerIds.join()) {
      this.subscribedContainerIds = subscribedContainerIds
      if (subscribedContainerIds.length) {
        logger.event('📢 subscribe/stats', {ids: subscribedContainerIds, target: 'container'})
        api.socket.emit('subscribe/stats', {ids: subscribedContainerIds, target: 'container'})
      } else {
        logger.event('📢 unsubscribe/stats', {target: 'container'})
        api.socket.emit('unsubscribe/stats', {target: 'container'})
      }
    }
  },
}

export default api
